import { z } from 'zod/v4';
import { USER_DEVELOPMENT_TAGS } from '../../../constants.js';

export const AdminUserPatchParamsSchema = z.object({
  id: z.string().min(1),
});

export type AdminUserPatchParams = z.infer<typeof AdminUserPatchParamsSchema>;

/**
 * Mirrors the legacy `patchById` body schema
 * (`backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`).
 * Unknown properties are silently stripped (zod's default object behaviour)
 * rather than rejected, matching the legacy Fastify quirk where
 * `additionalProperties: false` was never actually enforced.
 */
export const AdminUserPatchBodySchema = z.object({
  isBlocked: z.boolean().nullable().optional(),
  developmentTag: z.enum(USER_DEVELOPMENT_TAGS).optional(),
});

export type AdminUserPatchBody = z.infer<typeof AdminUserPatchBodySchema>;

const EnabledDevelopmentTagSchema = z.object({
  featureId: z.string(),
  tag: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isEnabled: z.number(),
});

/**
 * Row shape returned by the admin patch-user-by-id endpoint. `looseObject`
 * for the same reason as `AdminUserGetResponseSchema` — the underlying
 * `users` table has ~25 columns and this is a faithful pass-through of the
 * raw row. Unlike `AdminUserGetResponseSchema`, this has no `company` /
 * `preferences` / `userCompanies` — the legacy `updateUnscoped` this ports
 * only ever returns the bare user row plus `enabledDevelopmentTags`.
 */
export const AdminUserPatchResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  langId: z.string().nullable(),
  isBlocked: z.boolean(),
  developmentTag: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
  originalAvatarFileUrl: z.string().nullable(),
  enabledDevelopmentTags: z.array(EnabledDevelopmentTagSchema),
  updatedAt: z.union([z.string(), z.date()]).nullable(),
});

export type AdminUserPatchResponse = z.infer<
  typeof AdminUserPatchResponseSchema
>;
